import { FeatureFlags, FieldTypeOption } from './types';
import { HiOutlineMenuAlt2 } from 'react-icons/hi';
import { AlignLeft } from '@kula-ai/icons/dual-tone/AlignLeft';
import { CheckDone01 } from '@kula-ai/icons/dual-tone/CheckDone01';
import { ChevronSelectorVertical } from '@kula-ai/icons/outline/ChevronSelectorVertical';
import { Bs123 } from 'react-icons/bs';
import { Calendar } from '@kula-ai/icons/dual-tone/Calendar';
import { Toggle02Left } from '@kula-ai/icons/dual-tone/Toggle02Left';
import { Link03 } from '@kula-ai/icons/dual-tone/Link03';
import { Upload01 } from '@kula-ai/icons/dual-tone/Upload01';
import { Calculator } from '@kula-ai/icons/outline/Calculator';
import { Dotpoints02 } from '@kula-ai/icons/outline/Dotpoints02';
import { CurrencyDollar } from '@kula-ai/icons/outline/CurrencyDollar';
import { Mail01 } from '@kula-ai/icons/outline/Mail01';
import { Phone } from '@kula-ai/icons/outline/Phone';
import { AlignJustify } from '@kula-ai/icons/outline/AlignJustify';
import { Location } from 'react-iconly';
import { Bold02 } from '@kula-ai/icons/outline/Bold02';
import { Users01 } from '@kula-ai/icons/dual-tone/Users01';
// Common props for field options
const commonProps = {
  iconProps: {
    color: 'secondary-700',
    height: '16px !important',
    width: '16px !important',
  },
};

// Single source of truth for field type options
export const fieldTypeData: (FieldTypeOption & {
  label: string;
  formType?: string;
})[] = [
  // TEXT category
  {
    type: 'string',
    title: 'Short text',
    label: 'Short Text',
    category: 'TEXT',
    icon: HiOutlineMenuAlt2,
    bgColor: 'pastel-3-light',
    color: 'pastel-3-dark',
    formType: 'input',
    maxLength: 255,
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },
  {
    type: 'text',
    title: 'Long text',
    label: 'Long Text',
    category: 'TEXT',
    icon: AlignLeft,
    bgColor: 'pastel-3-light',
    color: 'pastel-3-dark',
    formType: 'textArea',
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },
  {
    type: 'richtext',
    title: 'Rich text',
    label: 'Rich Text',
    category: 'TEXT',
    icon: Bold02,
    bgColor: 'pastel-3-light',
    color: 'pastel-3-dark',
    formType: 'editor',
    subjectTypes: ['ats_offer'],
    featureFlag: 'feRichText',
    ...commonProps,
  },

  // CHOICES category
  {
    type: 'rel_tag',
    title: 'Single select',
    label: 'Single select',
    category: 'CHOICES',
    bgColor: 'pastel-6-light',
    icon: ChevronSelectorVertical,
    color: 'pastel-6-dark',
    formType: 'select',
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },
  {
    type: 'rel_tag_multi',
    title: 'Multi select',
    label: 'Multi Select',
    category: 'CHOICES',
    bgColor: 'pastel-6-light',
    icon: CheckDone01,
    color: 'pastel-6-dark',
    formType: 'select',
    multi: true,
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },
  {
    type: 'enum_boolean',
    title: 'Yes/No',
    label: 'Yes/No',
    category: 'CHOICES',
    bgColor: 'pastel-6-light',
    icon: Toggle02Left,
    color: 'pastel-6-dark',
    formType: 'radio2',
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },

  // DATE category
  {
    type: 'date',
    title: 'Date',
    label: 'Date',
    category: 'DATE',
    bgColor: 'pastel-2-light',
    icon: Calendar,
    color: 'pastel-2-dark',
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },

  // MISC category
  {
    type: 'number',
    title: 'Number',
    label: 'Number',
    category: 'MISC',
    bgColor: 'pastel-1-light',
    icon: Bs123,
    color: 'pastel-1-dark',
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },
  {
    type: 'url',
    title: 'URL',
    label: 'URL',
    category: 'MISC',
    bgColor: 'pastel-1-light',
    icon: Link03,
    color: 'pastel-1-dark',
    maxLength: 255,
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },
  {
    type: 'rel_file',
    title: 'File upload',
    label: 'File',
    category: 'MISC',
    bgColor: 'pastel-1-light',
    icon: Upload01,
    color: 'pastel-1-dark',
    formType: 'upload',
    subjectTypes: ['ats_offer', 'requisition', 'job_profile'],
    ...commonProps,
  },

  // USERS category — requisition-only "Users" (rel_user) field. Its selectable set is
  // configured by users or by roles; used to source approval-step reviewers.
  {
    type: 'rel_user',
    title: 'Users',
    label: 'Users',
    category: 'USERS',
    bgColor: 'pastel-5-light',
    icon: Users01,
    color: 'pastel-5-dark',
    formType: 'select',
    multi: true,
    subjectTypes: ['requisition'],
    ...commonProps,
  },
];

export const fieldTypeOptions: FieldTypeOption[] = fieldTypeData.map(
  ({ type, title, category, icon, bgColor, color, multi }) => ({
    type,
    title,
    category,
    icon,
    bgColor,
    color,
    multi,
  })
);

export const customFieldOptions = (
  subjectType: string = 'requisition',
  featureFlags?: FeatureFlags
) =>
  fieldTypeData
    .filter((field) => field.subjectTypes?.includes(subjectType))
    .filter((field) => {
      // If field has a featureFlag requirement, check if it's enabled
      if (field.featureFlag) {
        return featureFlags?.[field.featureFlag] === true;
      }
      return true;
    })
    ?.map((field) => ({
      value: field.type,
      label: field.label,
      type: field.formType,
      maxLength: field.maxLength,
      icon: field.icon,
      ...commonProps,
    })) || [];

export const customFieldsIconMap = {
  string: HiOutlineMenuAlt2,
  email: Mail01,
  phone: Phone,
  address: Location,
  rel_file: Upload01,
  singleText: AlignLeft,
  rel_file_multiple: Dotpoints02,
  complex: AlignJustify,
  rel_tag: ChevronSelectorVertical,
  rel_tag_multi: CheckDone01,
  rel_user: Users01,
  text: AlignLeft,
  currency: CurrencyDollar,
  number: Bs123,
  range: Calendar,
  enum_boolean: Toggle02Left,
  radio2: CheckDone01,
  textArea: AlignLeft,
  richtext: Bold02,
  url: Link03,
  date: Calendar,
  numberRange: Calculator,
  multiSelect: CheckDone01,
  integer: Dotpoints02,
  decimal: Bs123,
  composite: Dotpoints02,
};
